import React from 'react'
import styled from 'styled-components'
import { sidebarWidth, endpoints, schema, sdk, optional, trustandsafety, resource } from '../constants'
import { Button } from '../create-app-button'
import Dropdown from './dropdown'
import { Location } from '@reach/router'
import { TitleLink } from '../../types/title-link'
import ContextLink from '../context-link'
import { giphyCharcoal, giphyLightBlue } from '../../styles/colors'
import CreateNewApp from '../utils/qa-events'

const Container = styled.div`
    position: relative;
    display: flex;
    flex-shrink: 0;
    width: ${sidebarWidth}px;
    margin-right: 30px;
`

const FixedContainer = styled.div`
    position: sticky;
    top: 96px;

    display: flex;
    flex-direction: column;
    width: ${sidebarWidth}px;
    max-height: calc(100vh - 100px);
    margin-bottom: 20px;
`

const Navigation = styled.nav`
    max-height: 605px;
    padding: 20px 0 0 30px;
    margin-bottom: 15px;
    border: 0 solid black;
    font-size: 15px;
    background-color: rgba(225, 225, 225, 0.3);
    overflow-y: auto;

    > ul {
        margin-top: 0px;
        margin-bottom: 20px;
    }

    ul {
        list-style: none;
        padding-left: 0px;
        font-size: 15px;
    }

    li {
        margin-bottom: 6px;
    }

    a {
        line-height: 20px;
        padding-left: 15px;
        text-decoration: none;
        border: none;
        color: ${giphyCharcoal};

        &:hover {
            color: ${giphyLightBlue};
        }
    }
`

const Heading = styled.h6`
    font-weight: bold;
    line-height: 20px;
    margin-bottom: 6px;
`

const StyledButton = styled(Button)`
    width: auto;
    margin: 0;
    line-height: 36px;
`

const matchHashCollection = (hash: string, collection: TitleLink[]) =>
    Boolean(hash) && collection.some((item: TitleLink) => item.link.includes(hash))

const matchPathCollection = (path: string, collection: TitleLink[]) =>
    Boolean(path) && collection.some((item: TitleLink) => item.link === path)

const matchPaths = (path: string, to: string) => path.replace(/\//g, '') === to.replace(/\//g, '')

const Link = (route: string) => ({ to, children }: { to: string; children: string }) => (
    <ContextLink
        to={to}
        getProps={() => ({
            style: matchPaths(route, to) ? { color: giphyLightBlue, fontWeight: 'bold' } : null,
        })}
    >
        {children}
    </ContextLink>
)

const Sidebar = ({ route }: { route: string }) => {
    const SidebarLink = Link(route)

    return (
        <Container>
            <FixedContainer>
                <Navigation>
                    <Heading>GIPHY SDK</Heading>
                    <ul>
                        {sdk.map(({ title, link }, index) => (
                            <li key={index}>
                                <SidebarLink to={link}>{title}</SidebarLink>
                            </li>
                        ))}
                    </ul>

                    <Heading>GIPHY API</Heading>
                    <Location>
                        {({ location }) => (
                            <ul>
                                <li>
                                    <SidebarLink to="/docs/api#quick-start-guide">API Quickstart Guide</SidebarLink>
                                </li>
                                <li>
                                    <Dropdown
                                        title="API Endpoints"
                                        isOpen={matchHashCollection(location.hash, endpoints)}
                                        isActive={matchPathCollection(route, endpoints)}
                                    >
                                        <ul>
                                            {endpoints.map(({ title, link }, index) => (
                                                <li key={index}>
                                                    <SidebarLink to={link}>{title}</SidebarLink>
                                                </li>
                                            ))}
                                        </ul>
                                    </Dropdown>
                                </li>
                                <li>
                                    <Dropdown
                                        title="Schema Definitions"
                                        isOpen={matchHashCollection(location.hash, schema)}
                                        isActive={matchPathCollection(route, schema)}
                                    >
                                        <ul>
                                            {schema.map(({ title, link }, index) => (
                                                <li key={index}>
                                                    <SidebarLink to={link}>{title}</SidebarLink>
                                                </li>
                                            ))}
                                        </ul>
                                    </Dropdown>
                                </li>
                                <li>
                                    <SidebarLink to="/docs/api#response-codes">Response Code</SidebarLink>
                                </li>
                            </ul>
                        )}
                    </Location>
                    <Heading>Optional Settings</Heading>
                    <ul>
                        {optional.map(({ title, link }, index) => (
                            <li key={index}>
                                <SidebarLink to={link}>{title}</SidebarLink>
                            </li>
                        ))}
                    </ul>
                    <Heading>Trust And Safety</Heading>
                    <ul>
                        {trustandsafety.map(({ title, link }, index) => (
                            <li key={index}>
                                <SidebarLink to={link}>{title}</SidebarLink>
                            </li>
                        ))}
                    </ul>

                    <Heading>Resources</Heading>
                    <ul>
                        {resource.map(({ title, link }, index) => (
                            <li key={index}>
                                <SidebarLink to={link}>{title}</SidebarLink>
                            </li>
                        ))}
                    </ul>
                </Navigation>
                <StyledButton href="/dashboard/?create=true" onClick={CreateNewApp}>
                    Create an App
                </StyledButton>
            </FixedContainer>
        </Container>
    )
}

export default Sidebar
